import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import MembersSidebar from '../components/MembersSidebar';

const MembersAddresses = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);

  useEffect(() => {
    const memberData = localStorage.getItem('member_data');
    if (memberData) {
      const parsedMember = JSON.parse(memberData);
      setMember(parsedMember);
    }
    
    // Try to get addresses from localStorage
    const savedAddresses = localStorage.getItem('member_addresses');
    if (savedAddresses) {
      setAddresses(JSON.parse(savedAddresses));
    }
    
    setLoading(false);
  }, []);

  const handleLogout = async () => {
    setLogoutLoading(true);
    
    try {
      const token = localStorage.getItem('member_token');
      if (token) {
        await axios.post('https://backend.dlapakistan.com/api/members/logout/', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.log('Logout API error:', error);
    }
    
    localStorage.removeItem('member_token');
    localStorage.removeItem('member_data');
    
    if (axios.defaults.headers.common['Authorization']) {
      delete axios.defaults.headers.common['Authorization'];
    }
    
    Swal.fire({
      icon: 'success',
      title: 'Signed Out',
      text: 'You have been signed out successfully.',
      timer: 2000,
      showConfirmButton: false,
    });
    
    navigate('/members/members-area');
    setLogoutLoading(false);
  };

  const handleRemoveAddress = (addressId) => {
    const updatedAddresses = addresses.filter(address => address.id !== addressId);
    setAddresses(updatedAddresses);
    localStorage.setItem('member_addresses', JSON.stringify(updatedAddresses));
  };

  const handleAddAddress = () => {
    navigate('/dlashop/checkout'); // Change to your add address page
  };

  const handleEditAddress = (addressId) => {
    // Navigate to edit address page
    console.log('Edit address:', addressId);
  };

  if (!member) {
    return (
      <Container fluid className="py-5 min-vh-100 mt-40" style={{ maxWidth: '95%' }}>
        <div className="text-center py-5">
          <p>Loading...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-5 min-vh-100 mt-40" style={{ maxWidth: '95%' }}>
      {/* Header with user info and logout - EXACTLY like other pages */}
      <div className="mb-6">
        <h1 className="display-6 text-dark mb-3">
          Hello {member.first_name} {member.last_name}
        </h1>
        <p className="text-muted mb-3">
          Manage Account Details!{' '}{' '}{' '}
          <Button 
            variant="link" 
            onClick={handleLogout}
            disabled={logoutLoading}
            className="text-danger p-0"
            style={{ textDecoration: 'none' }}
          >
            {logoutLoading ? 'Signing out...' : 'Log out'}
          </Button>
        </p>
      </div>

      <Row className="g-4">
        {/* Sidebar Column - SAME as other pages */}
        <Col lg={3} md={4}>
          <MembersSidebar />
        </Col>

        {/* Content Column - SAME as other pages */}
        <Col lg={9} md={8}>
          <div className="bg-white shadow-sm rounded-lg p-4 p-lg-5">
            <div className="text-left">
              <h4 className="text-[#70B444] mb-4">My Addresses</h4>
              
              {loading ? (
                <div className="text-center py-4">
                  <p>Loading addresses...</p>
                </div>
              ) : addresses.length === 0 ? (
                // Display when no addresses exist - SAME as other pages
                <div className="text-center py-5">
                  <h5 className="text-muted mb-3">No addresses saved yet.</h5>
                  <p className="text-muted mb-4">
                    You haven't saved any addresses yet. Add your shipping and billing addresses.
                  </p>
                  <Button 
                    variant="success" 
                    onClick={handleAddAddress}
                    className="px-4"
                  >
                    Add Address
                  </Button>
                </div>
              ) : (
                // Display when addresses exist
                <>
                  <div className="mb-4">
                    <p className="text-gray-700 mb-0">
                      Manage your shipping and billing addresses below. You can add, edit, or remove addresses.
                    </p>
                  </div>
                  
                  <Row className="g-4">
                    {addresses.map((address) => (
                      <Col md={6} key={address.id}>
                        <Card className="h-100 border">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h5 className="mb-1">
                                  {address.type === 'shipping' ? 'Shipping Address' : 'Billing Address'}
                                  {address.isDefault && (
                                    <span className="badge bg-success ms-2">Default</span>
                                  )}
                                </h5>
                                <small className="text-muted">
                                  Added: {new Date(address.createdAt).toLocaleDateString()}
                                </small>
                              </div>
                              <Button
                                variant="link"
                                onClick={() => handleRemoveAddress(address.id)}
                                className="text-danger p-0"
                                style={{ textDecoration: 'none' }}
                              >
                                ❌
                              </Button>
                            </div>
                            
                            <div className="address-details">
                              <p className="mb-1"><strong>{address.fullName}</strong></p>
                              <p className="mb-1">{address.addressLine1}</p>
                              {address.addressLine2 && (
                                <p className="mb-1">{address.addressLine2}</p>
                              )}
                              <p className="mb-1">
                                {address.city}, {address.state} {address.zipCode}
                              </p>
                              <p className="mb-1">{address.country}</p>
                              <p className="mb-0">
                                <strong>Phone:</strong> {address.phone}
                              </p>
                              {address.email && (
                                <p className="mb-0">
                                  <strong>Email:</strong> {address.email}
                                </p>
                              )}
                            </div>
                            
                            <div className="d-flex justify-content-between mt-3 pt-3 border-top">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleEditAddress(address.id)}
                              >
                                Edit
                              </Button>
                              {!address.isDefault && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => console.log('Set as default:', address.id)}
                                >
                                  Set as Default
                                </Button>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  
                  <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => navigate('/members-home')}
                      className="px-4"
                    >
                      Back to Dashboard
                    </Button>
                    <Button 
                      variant="success" 
                      onClick={handleAddAddress}
                      className="px-4"
                    >
                      Add New Address
                    </Button>
                  </div>
                </>
              )}
              
              <p className="text-muted mb-0 mt-4">
                Select an option from the sidebar to manage other account settings.
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default MembersAddresses;